import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Phone, Key, User, ArrowRight, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentRegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [step, setStep] = useState(1); // 1: Mobile, 2: OTP, 3: Details
  const [isLoading, setIsLoading] = useState(false);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    if (mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { mobile: `+91${mobile}` });
      toast.success('OTP sent to your mobile number');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (otp.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { 
        mobile: `+91${mobile}`, 
        otp 
      });
      setTempToken(response.data.temp_token);
      toast.success('Mobile verified successfully!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Complete Registration
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register-student`, {
        temp_token: tempToken,
        name: formData.name,
        email: formData.email || null,
        password: formData.password,
        mobile: `+91${mobile}`
      });
      
      login(response.data.user, response.data.token);
      toast.success('Registration successful! Please complete your profile.');
      navigate('/student/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resendOTP = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { mobile: `+91${mobile}` });
      toast.success('OTP resent successfully');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Logo */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-blue-600">Iqra</span>
              <span className="text-emerald-600">skill</span>
            </h1>
            <p className="text-slate-600 mt-2">Student Registration</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {step > 1 ? <CheckCircle className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
              </div>
              <div className={`w-16 h-1 ${step > 1 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                {step > 2 ? <CheckCircle className="h-5 w-5" /> : <Key className="h-5 w-5" />}
              </div>
              <div className={`w-16 h-1 ${step > 2 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                <User className="h-5 w-5" />
              </div>
            </div>
          </div>

          {/* Step 1: Mobile Number */}
          {step === 1 && (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Enter Mobile Number</h2>
                <p className="text-sm text-slate-600">We'll send you an OTP for verification</p>
              </div>

              <div>
                <Label htmlFor="mobile">Mobile Number (मोबाइल नंबर) *</Label>
                <div className="flex mt-1">
                  <span className="inline-flex items-center px-4 bg-slate-100 border border-r-0 border-slate-300 rounded-l-md text-slate-600 font-medium">
                    +91
                  </span>
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="9876543210"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="rounded-l-none text-lg"
                    maxLength={10}
                    required
                    data-testid="mobile-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading || mobile.length !== 10}
                className="w-full bg-blue-600 hover:bg-blue-700 mt-6"
                data-testid="send-otp-btn"
              >
                {isLoading ? 'Sending OTP...' : (
                  <>
                    Send OTP <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Key className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Verify OTP</h2>
                <p className="text-sm text-slate-600">Enter the 4-digit code sent to +91{mobile}</p>
              </div>

              <div>
                <Label htmlFor="otp">OTP Code</Label>
                <Input
                  id="otp"
                  type="text"
                  placeholder="Enter 4-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  className="text-center text-2xl tracking-widest mt-1"
                  maxLength={4}
                  required
                  data-testid="otp-input"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Demo Mode:</strong> Use OTP <code className="bg-amber-100 px-2 py-0.5 rounded">717715</code>
              </div>

              <Button
                type="submit"
                disabled={isLoading || otp.length !== 4}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="verify-otp-btn"
              >
                {isLoading ? 'Verifying...' : 'Verify OTP'}
              </Button>

              <div className="flex justify-between items-center text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-slate-600 hover:text-blue-600"
                >
                  Change Number
                </button>
                <button
                  type="button"
                  onClick={resendOTP}
                  disabled={isLoading}
                  className="text-blue-600 hover:text-blue-700"
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Personal Details */}
          {step === 3 && (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="h-8 w-8 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Complete Registration</h2>
                <p className="text-sm text-slate-600">Fill in your details to create your account</p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Mobile verified: +91{mobile}</span>
              </div>

              <div>
                <Label htmlFor="name">Full Name (पूरा नाम) *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email ID (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  minLength={6}
                  className="mt-1"
                  data-testid="password-input"
                />
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 mt-6"
                data-testid="register-btn"
              >
                {isLoading ? 'Creating Account...' : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Create Account
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Footer Links */}
          <div className="mt-6 text-center text-sm text-slate-600">
            <p>
              Already have an account?{' '}
              <Link to="/auth/login" className="text-blue-600 hover:underline font-medium">
                Login
              </Link>
            </p>
            <p className="mt-2">
              Are you an Employer?{' '}
              <Link to="/auth/register" className="text-emerald-600 hover:underline font-medium">
                Register as Employer
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRegisterPage;
